import { Coordinates, ObservatoryRow, HashMap, ObservatoryType, ObservatoryValue } from 'common/types'
import { StageAlertLevel, isPrivateMode } from 'common/consts'
import * as api from 'common/api'
import UTIF from 'utif'

export const isDevelopment = process.env.NODE_ENV === 'development'

export const convertCoordinates = (bounds: [[number, number], [number, number]]): Coordinates => {
  const northWest = bounds[0]
  const southEast = bounds[1]
  return [
    [northWest[1], northWest[0]],
    [southEast[1], northWest[0]],
    [southEast[1], southEast[0]],
    [northWest[1], southEast[0]],
  ]
}

export const getSinsuiDisplayValue = (value: string) => {
  if (!value) {
    return ''
  } else if (value === '~0.5m') {
    return `${value} (1階床下)`
  } else if (value === '0.5m~3.0m') {
    return `${value} (1階浸水程度)`
  } else if (value === '3.0m~5.0m') {
    return `${value} (2階浸水程度)`
  } else {
    return `${value} (3階以上浸水程度)`
  }
}

export const compareHelper = (order: { key: string; reverse: boolean }[]) => {
  return (a: any, b: any) => {
    for (let i = 0; i < order.length; i++) {
      const order_by = order[i].reverse ? 1 : -1
      if (a[order[i].key] < b[order[i].key]) return order_by
      if (a[order[i].key] > b[order[i].key]) return order_by * -1
    }
    return 0
  }
}

export const downloadImage = (url: string) => {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = e => reject(e)
    image.src = url
  })
}

export const loadBinaryStreamImage = (imageUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = function() {
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      let url = ''
      if (context) {
        canvas.width = image.width
        canvas.height = image.height
        context.clearRect(0, 0, image.width, image.height)
        context.drawImage(image, 0, 0, image.width, image.height, 0, 0, image.width, image.height)
        url = canvas.toDataURL('image/png')
      }
      resolve(url)
    }
    image.onerror = function() {
      // reject(new Error('Failed to load image.'))
    }
    image.src = imageUrl
  })
}

export const splitImage = (imageUrl: string, numParts: number): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = function() {
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      const imageWidth = image.width
      const imageHeight = image.height
      const partHeight = imageHeight / numParts
      const partUrls: string[] = []
      if (context) {
        for (let i = 0; i < numParts; i++) {
          canvas.width = imageWidth
          canvas.height = partHeight
          context.clearRect(0, 0, imageWidth, partHeight)
          context.drawImage(image, 0, i * partHeight, imageWidth, partHeight, 0, 0, imageWidth, partHeight)
          const partDataUrl = canvas.toDataURL('image/png')
          partUrls.push(partDataUrl)
        }
      }
      resolve(partUrls)
    }
    image.onerror = function() {
      reject(new Error('Failed to load image.'))
    }
    image.src = imageUrl
  })
}

export const splitImageWithCoordinates = async (
  imageUrl: string,
  coordinates: Coordinates,
  splitNum = 10
): Promise<{url: string, coordinates: Coordinates}[]> => {
  const north = coordinates[0][1]
  const west = coordinates[0][0]
  const south = coordinates[2][1]
  const east = coordinates[2][0]

  try {
    let urls: string[] = []
    if (imageUrl.endsWith('.tif')) {
      const r = await fetch(imageUrl)
      if (r.ok) {
        const buffer = await r.arrayBuffer()
        const ifds = UTIF.decode(buffer)
        UTIF.decodeImage(buffer, ifds[0])
        const rgba = UTIF.toRGBA8(ifds[0])
        const tiffWidth = ifds[0].width
        const tiffHeight = ifds[0].height
        const tiffImage = new ImageData(new Uint8ClampedArray(rgba), tiffWidth, tiffHeight)

        const tiffCanvas = document.createElement('canvas')
        tiffCanvas.width = tiffWidth
        tiffCanvas.height = tiffHeight
        const tiffContext = tiffCanvas.getContext('2d')
        if (tiffContext) tiffContext.putImageData(tiffImage, 0, 0)

        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')
        const partHeight = tiffHeight/ splitNum
        if (context) {
          for (let i = 0; i < splitNum; i++) {
            canvas.width = tiffWidth
            canvas.height = partHeight
            context.clearRect(0, 0, tiffWidth, partHeight)
            context.drawImage(tiffCanvas, 0, i * partHeight, tiffWidth, partHeight, 0, 0, tiffWidth, partHeight)
            const partDataUrl = canvas.toDataURL('image/png')
            urls.push(partDataUrl)
          }
        }
      }
    } else {
      urls = await splitImage(imageUrl, splitNum)
    }
    return urls.map((url, i) => {
      const offset = (north - south) / urls.length
      const lat1 = north - (offset * i)
      const lat2 = north - (offset * (i + 1))
      return {
        url: url,
        coordinates: [
          [west, lat1],
          [east, lat1],
          [east, lat2],
          [west, lat2]
        ]
      }
    })
  } catch (error) {
    return []
  }
}

export const getQueryRate = (): string | null => {
  const queryParams = new URLSearchParams(location.search)
  return queryParams.get('rate')
}

export const getAppTitle = (): string => {
  if (!process.env.REACT_APP_NAME) {
    return '巴川予測システム'
  }
  if (isPrivateMode) {
    const rate = getQueryRate()
    if (rate === '1.5') {
      return process.env.REACT_APP_NAME + ' (1.5倍予測)'
    } else if (rate === '2.0') {
      return process.env.REACT_APP_NAME + ' (2.0倍予測)'
    }
  }
  return process.env.REACT_APP_NAME
}

export const fetchObservatoryTimeSeries = async (
  target: ObservatoryRow,
  date: string
): Promise<ObservatoryValue[]> => {
  const params: HashMap = {
    date: date,
    id: target.id,
    type: target.type,
  }
  if (target.type === ObservatoryType.Rain) {
    params.rain_forecast = true
    params.hours = 24
    params.lat = target.lat
    params.lng = target.lng
  }
  else if (target.type === ObservatoryType.Stage || target.type === ObservatoryType.Pump) {
    if (isPrivateMode) {
      params.bucket = 'ctie.shizuoka.web'
      params.prefix = 'predict_stage'
      const rate = getQueryRate()
      if (rate === '1.5') {
        params.prefix = 'predict_stage_6h_1.5'
      } else if (rate === '2.0') {
        params.prefix = 'predict_stage_6h_2.0'
      }
    }
  }

  if (target.dynamodb) {
    params.dynamodb = target.dynamodb
  }
  if (target.bucket) {
    params.bucket = target.bucket
  }
  if (target.prefix) {
    params.prefix = target.prefix
  }

  const response = await api.get('/observatories/data', params)
  if (response === null) {
    return []
  }

  const timeSeries: ObservatoryValue[] = response.timeSeries
  timeSeries.forEach((ts: ObservatoryValue) => {
    if (ts.level !== undefined) ts.level = Number(ts.level)
    if (ts.min10 !== undefined) ts.min10 = Number(ts.min10)
    if (ts.min60 !== undefined) ts.min60 = Number(ts.min60)
    if (ts.total !== undefined) ts.total = Number(ts.total)
  })
  return timeSeries
}

export const getStageStatus = (obs: ObservatoryRow, level: number | undefined): string => {
  if (level === undefined) {
    return StageAlertLevel.DEFICIT.key
  }

  if (obs.outbreakLevel !== undefined && level >= obs.outbreakLevel) {
    return StageAlertLevel.OUTBREAK.key
  } else if (obs.floodLevel !== undefined && level >= obs.floodLevel) {
    return StageAlertLevel.FLOOD.key
  } else if (obs.dangerousLevel !== undefined && level >= obs.dangerousLevel) {
    return StageAlertLevel.DANGEROUS.key
  } else if (obs.evacuationLevel !== undefined && level >= obs.evacuationLevel) {
    return StageAlertLevel.EVACUATION.key
  } else if (obs.warningLevel !== undefined && level >= obs.warningLevel) {
    return StageAlertLevel.WARNING.key
  } else if (obs.standbyLevel !== undefined && level >= obs.standbyLevel) {
    return StageAlertLevel.STANDBY.key
  } else if (obs.warnLevel !== undefined && level >= obs.warnLevel) {
    return StageAlertLevel.WARN.key
  } else if (obs.startLevel !== undefined && level >= obs.startLevel) {
    return StageAlertLevel.START.key
  } else {
    return StageAlertLevel.NORMAL.key
  }
}

export const getPolygonCenter = (coords: GeoJSON.Position[][]): [number, number] => {
  if (!coords) return [0, 0]
  let x = 0, y = 0
  const points = coords[0]
  points.forEach(point => {
    x += point[0]
    y += point[1]
  })
  return [x / points.length, y / points.length]
}
